/**
 * The files a project actually has — UX-05.
 *
 * `GET /v1/projects/{id}/files` has existed since storage shipped, with an
 * MCP tool and a CLI command in front of it. The dashboard had neither, so
 * the page called "Storage" could show buckets and policies and not one
 * object.
 *
 * Every judgement it makes lives in lib/files.ts, where tests hold it.
 * What is here is layout and the asking.
 */

import { useCallback, useEffect, useState } from 'react'
import { useApi } from '../lib/useApi.js'
import { ApiError } from '../lib/api.js'
import { Empty, ErrorBox, Loading } from './ui.js'
import {
  NO_TRANSFER_NOTE, browse, crumbs, emptyMessage, formatSize, type StoredObject,
} from '../lib/files.js'

const PAGE = 100

export function FileBrowser({ projectId }: { projectId: string }) {
  const api = useApi()
  const [objects, setObjects] = useState<StoredObject[]>([])
  const [prefix, setPrefix] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (at: string, next?: string) => {
    setLoading(true)
    setError(null)
    try {
      const page = await api.listFiles(projectId, {
        limit: PAGE, ...(at ? { prefix: at } : {}), ...(next ? { cursor: next } : {}),
      })
      // Appended when paging, replaced when moving: a "load more" that
      // dropped the rows above it would lose somebody's place.
      setObjects((prev) => (next ? [...prev, ...page.objects] : page.objects))
      setCursor(page.cursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read this project’s files.')
    } finally {
      setLoading(false)
    }
  }, [api, projectId])

  useEffect(() => { void load(prefix) }, [load, prefix])

  const listing = browse(objects, prefix)
  const trail = crumbs(prefix)

  return (
    <section style={{ marginTop: 24 }}>
      <h2>Files</h2>
      <p className="sub small">{NO_TRANSFER_NOTE}</p>

      <nav className="sub small" style={{ margin: '12px 0' }}>
        {trail.map((step, i) => (
          <span key={step.prefix}>
            {i > 0 && ' / '}
            {i === trail.length - 1 ? (
              <span className="mono">{step.label}</span>
            ) : (
              <button
                className="btn btn-small"
                onClick={() => { setCursor(null); setPrefix(step.prefix) }}
              >
                {step.label}
              </button>
            )}
          </span>
        ))}
      </nav>

      {error && <ErrorBox>{error}</ErrorBox>}

      {loading && objects.length === 0 ? <Loading /> : (
        listing.folders.length === 0 && listing.files.length === 0 ? (
          <Empty>{emptyMessage(prefix)}</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Size</th><th>Uploaded</th></tr>
            </thead>
            <tbody>
              {listing.folders.map((folder) => (
                <tr key={folder}>
                  <td colSpan={4}>
                    <button
                      className="btn btn-small"
                      onClick={() => { setCursor(null); setPrefix(`${prefix}${folder}`) }}
                    >
                      {folder}
                    </button>
                  </td>
                </tr>
              ))}
              {listing.files.map((file) => (
                <tr key={file.key}>
                  <td className="mono">{file.key.slice(prefix.length)}</td>
                  <td className="sub small">{file.contentType ?? '—'}</td>
                  <td className="mono">{formatSize(file.size)}</td>
                  <td className="sub small">{new Date(file.uploaded).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {cursor && (
        <button
          className="btn"
          style={{ marginTop: 12 }}
          disabled={loading}
          onClick={() => void load(prefix, cursor)}
        >
          {loading ? 'Loading…' : 'Show more'}
        </button>
      )}
    </section>
  )
}
